import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  useTheme,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikControl from "src/components/formik/FormikControl";
import dayjs from "dayjs";

interface HolidayFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  initialValues?: any;
}

export const HolidayFormModal = ({
  open,
  onClose,
  onSubmit,
  initialValues,
}: HolidayFormModalProps) => {
  const theme = useTheme();

  const defaultValues = {
    name: "",
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    isRecurring: false,
    description: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Holiday name is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date cannot be before start date"),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* 🔹 Colored Header */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          p: 2.5,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {initialValues ? "Update Holiday" : "Create Holiday"}
        </Typography>
      </Box>

      <Formik
        initialValues={{ ...defaultValues, ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmit(values);
          onClose();
        }}
      >
        {({ values, setFieldValue, getFieldProps }) => (
          <Form style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <DialogContent sx={{ flex: 1, overflowY: "auto", mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikControl
                    control="InputField"
                    label="Holiday Name"
                    name="name"
                    required
                    {...getFieldProps("name")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="CalendarTime"
                    label="Start Date"
                    dateOrTimeOnly="date"
                    name="startDate"
                    {...getFieldProps("startDate")}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormikControl
                    control="CalendarTime"
                    label="End Date"
                    dateOrTimeOnly="date"
                    name="endDate"
                    {...getFieldProps("endDate")}
                  />
                </Grid>

                {/* 🔹 Checkbox for Recurring */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isRecurring}
                        onChange={(e) =>
                          setFieldValue("isRecurring", e.target.checked)
                        }
                      />
                    }
                    label="Recurring"
                  />
                </Grid>

                {/* 🔹 Larger Textarea for Description */}
                <Grid item xs={12}>
                  <FormikControl
                    control="InputField"
                    label="Description"
                    name="description"
                    multiline
                    rows={5}
                    {...getFieldProps("description")}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
